/**
 * Consumer hooks for vocabulary feature.
 * Simple interface for UI components.
 */

import { useMemo, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useVocabularyCategoriesQuery,
  useVocabularyWordsQuery,
  useVocabularyPracticeQuery,
  useUpdateProgressMutation,
  useCreateQuizSessionMutation,
  useSubmitAnswerMutation,
} from "@/infrastructure/adapters/vocabulary/vocabulary.query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TVocabularyWord, TVocabularySummary, TVocabularyCategory } from "@/infrastructure/adapters/vocabulary/vocabulary.types";

/** Hook for vocabulary categories */
export const useVocabularyCategories = (options?: { enabled?: boolean }) => {
  const categoriesQuery = useVocabularyCategoriesQuery(options);

  const categories: TVocabularyCategory[] | null = useMemo(() => {
    if (categoriesQuery.data === undefined) return null;
    return categoriesQuery.data;
  }, [categoriesQuery.data]);

  return {
    categories,
    isLoading: categoriesQuery.isLoading,
    error: categoriesQuery.error,
  };
};

/** Main hook — words + summary derived from single API call */
export const useVocabulary = (category?: string, options?: { enabled?: boolean }) => {
  const wordsQuery = useVocabularyWordsQuery(category, options);

  /** Sort: mastered first, then by id ascending for consistent order */
  const words: TVocabularyWord[] = useMemo(() => {
    if (!wordsQuery.data) return [];
    return [...wordsQuery.data].sort((a, b) => {
      if (a.status !== b.status) return a.status === "mastered" ? -1 : 1;
      return Number(a.id) - Number(b.id);
    });
  }, [wordsQuery.data]);

  const summary: TVocabularySummary | null = useMemo(() => {
    if (!wordsQuery.data) return null;
    const masteredCount = wordsQuery.data.filter((w) => w.status === "mastered").length;
    return { masteredCount, totalCount: wordsQuery.data.length };
  }, [wordsQuery.data]);

  return {
    words,
    summary,
    isLoading: wordsQuery.isLoading,
    error: wordsQuery.error,
  };
};

const PRACTICE_LIMIT = 10;

/** Consumer hook for vocabulary practice session (BE returns limited words) */
export const useVocabularyPractice = (category?: string) => {
  const queryClient = useQueryClient();
  const practiceQuery = useVocabularyPracticeQuery(PRACTICE_LIMIT, category);
  const updateProgressMutation = useUpdateProgressMutation();

  const words: TVocabularyWord[] = useMemo(() => practiceQuery.data?.words ?? [], [practiceQuery.data]);
  const totalCount = practiceQuery.data?.totalCount ?? 0;
  const isPracticeQueryError = !!practiceQuery.error;

  /** Reset categories & words cache on error so Navigate back fetches fresh data */
  useEffect(() => {
    if (isPracticeQueryError) {
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.VOCABULARY.CATEGORIES] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.VOCABULARY.WORDS] });
    }
  }, [isPracticeQueryError, queryClient]);

  /** Clear cached practice data and fetch fresh words from API (single request) */
  const resetAndRefetch = useCallback(() => {
    queryClient.resetQueries({ queryKey: [QUERY_KEYS.VOCABULARY.PRACTICE_SESSION] });
  }, [queryClient]);

  return {
    words,
    totalCount,
    isLoading: practiceQuery.isLoading,
    error: practiceQuery.error,
    updateProgress: updateProgressMutation.mutate,
    resetAndRefetch,
  };
};

/** Consumer hook for vocabulary quiz (session-based flow) */
export const useVocabularyQuiz = () => {
  const queryClient = useQueryClient();
  const createSessionMutation = useCreateQuizSessionMutation();
  const submitAnswerMutation = useSubmitAnswerMutation();
  const isCreatingSessionError = !!createSessionMutation.error;

  /** Reset categories & words cache on session creation error so navigation back fetches fresh data */
  useEffect(() => {
    if (isCreatingSessionError) {
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.VOCABULARY.CATEGORIES] });
      queryClient.resetQueries({ queryKey: [QUERY_KEYS.VOCABULARY.WORDS] });
    }
  }, [isCreatingSessionError, queryClient]);

  return {
    createSession: createSessionMutation.mutateAsync,
    isCreatingSession: createSessionMutation.isPending,
    submitAnswer: submitAnswerMutation.mutate,
    submitAnswerAsync: submitAnswerMutation.mutateAsync,
  };
};
